import { supabase } from '@/lib/supabase';

export interface UserAPIConnection {
  id: string;
  apiName: string;
  status: string;
  lastRefreshAttempt: string | null;
  refreshStatus: string | null;
  balanceSyncStatus: string | null;
  createdAt: string;
}

export interface UserTradingAccount {
  id: string;
  apiConnectionId: string;
  accountNumber: string;
  balance: number;
  isLive: boolean;
  lastBalanceUpdate: string | null;
  previousBalance: number | null;
}

export interface UserBot {
  id: string;
  name: string;
  type: string;
  status: string;
  createdDate: string;
  connectedAccountsCount: number;
  // Premium bot details
  winrate?: string;
  profitFactor?: string;
  subscribersTotal?: number;
  // Prop bot details
  passRate?: number;
  propFirm?: string;
  challengeDuration?: string;
  // Trading details
  tradingStyle?: string;
  timeframe?: string;
  markets?: string[];
}

export interface UserBotAccount {
  id: string;
  botId: string;
  tradingAccountId: string;
  volumeMultiplier: number;
  isReversePosition: boolean;
  status: string;
  // Additional info from joins
  botName?: string;
  botType?: string;
  balance?: number;
  isLive?: boolean;
  broker?: string;
  propFirm?: string;
}

export interface UserAIUsage {
  totalMessages: number;
  totalAnalysisSessions: number;
  topicUsage: Array<{
    topic: string;
    messageCount: number;
    dailyLimit: number;
    monthlyLimit: number;
  }>;
  recentUsage: Array<{
    date: string;
    messageCount: number;
    analysisCount: number;
  }>;
}

export interface UserSubscriptionDetail {
  id: string;
  userId: string;
  packageId: string;
  startDate: string;
  endDate: string;
  status: string;
  renewalType: string;
  paymentMethod: string;
  paymentStatus: string;
  packageName: string;
  packagePrice: number;
}

export interface UserLoginHistory {
  sessionId: string;
  createdAt: string;
  lastActivity: string;
  ipAddress?: string;
  userAgent?: string;
}

export const userDetailService = {
  // Trading Setup Tab
  async getUserAPIConnections(userId: string): Promise<UserAPIConnection[]> {
    try {
      const { data, error } = await supabase
        .from('api_connections')
        .select(`
          id,
          api_name,
          status,
          last_refresh_attempt,
          refresh_status,
          balance_sync_status,
          created_at,
          csp_accounts!inner(user_id)
        `)
        .eq('csp_accounts.user_id', userId)
        .order('created_at', { ascending: false });

      if (error) throw error;

      return data?.map(conn => ({
        id: conn.id,
        apiName: conn.api_name,
        status: conn.status,
        lastRefreshAttempt: conn.last_refresh_attempt,
        refreshStatus: conn.refresh_status,
        balanceSyncStatus: conn.balance_sync_status,
        createdAt: conn.created_at
      })) || [];
    } catch (error) {
      console.error('Error fetching user API connections:', error);
      return [];
    }
  },

  async getUserTradingAccounts(userId: string): Promise<UserTradingAccount[]> {
    try {
      const { data, error } = await supabase
        .from('trading_accounts')
        .select(`
          id,
          api_connection_id,
          number_account_trading,
          balance_account,
          live,
          last_balance_update,
          previous_balance,
          api_connections!inner(
            id,
            csp_accounts!inner(user_id)
          )
        `)
        .eq('api_connections.csp_accounts.user_id', userId)
        .order('created_at', { ascending: false });

      if (error) throw error;

      return data?.map(account => ({
        id: account.id,
        apiConnectionId: account.api_connection_id,
        accountNumber: account.number_account_trading,
        balance: account.balance_account || 0,
        isLive: account.live || false,
        lastBalanceUpdate: account.last_balance_update,
        previousBalance: account.previous_balance
      })) || [];
    } catch (error) {
      console.error('Error fetching user trading accounts:', error);
      return [];
    }
  },

  // Bots & Performance Tab
  async getUserBots(userId: string): Promise<UserBot[]> {
    try {
      // Get bots user is using via bot_accounts (join via original_bot_id for shadow bot support)
      const { data: botAccountsData, error: botAccountsError } = await supabase
        .from('bot_accounts')
        .select(`
          bot_id,
          original_bot_id,
          bots!bot_accounts_original_bot_id_fkey(
            id,
            name,
            type,
            status,
            created_date,
            bot_premium_details(
              winrate,
              profit_factor,
              subscribers_total,
              trading_style,
              timeframe,
              markets
            ),
            bot_prop_details(
              pass_rate,
              prop_firm,
              challenge_duration,
              trading_style,
              timeframe,
              markets
            )
          )
        `)
        .eq('user_id', userId);

      if (botAccountsError) throw botAccountsError;

      if (!botAccountsData || botAccountsData.length === 0) {
        return [];
      }

      // Count accounts per bot
      const accountCountMap = new Map();
      botAccountsData.forEach(account => {
        const current = accountCountMap.get(account.bot_id) || 0;
        accountCountMap.set(account.bot_id, current + 1);
      });

      // Process unique bots
      const uniqueBots = new Map();
      botAccountsData.forEach(account => {
        const bot = Array.isArray(account.bots) ? account.bots[0] : account.bots;
        if (bot && !uniqueBots.has(bot.id)) {
          const premiumDetails = Array.isArray(bot.bot_premium_details) ? bot.bot_premium_details[0] : bot.bot_premium_details;
          const propDetails = Array.isArray(bot.bot_prop_details) ? bot.bot_prop_details[0] : bot.bot_prop_details;
          
          uniqueBots.set(bot.id, {
            id: bot.id,
            name: bot.name,
            type: bot.type,
            status: bot.status,
            createdDate: bot.created_date,
            connectedAccountsCount: accountCountMap.get(bot.id) || 0,
            // Premium bot fields
            winrate: premiumDetails?.winrate,
            profitFactor: premiumDetails?.profit_factor,
            subscribersTotal: premiumDetails?.subscribers_total,
            // Prop bot fields
            passRate: propDetails?.pass_rate,
            propFirm: propDetails?.prop_firm,
            challengeDuration: propDetails?.challenge_duration,
            // Trading details (from either premium or prop)
            tradingStyle: premiumDetails?.trading_style || propDetails?.trading_style,
            timeframe: premiumDetails?.timeframe || propDetails?.timeframe,
            markets: premiumDetails?.markets || propDetails?.markets
          });
        }
      });

      return Array.from(uniqueBots.values());
    } catch (error) {
      console.error('Error fetching user bots:', error);
      return [];
    }
  },

  async getUserBotAccounts(userId: string): Promise<UserBotAccount[]> {
    try {
      // Step 1: Get bot accounts with bot info
      const { data: botAccountsData, error: botAccountsError } = await supabase
        .from('bot_accounts')
        .select(`
          id,
          bot_id,
          original_bot_id,
          accountid_trading,
          volume_multiplier,
          is_reverse_position,
          status,
          bots!bot_accounts_original_bot_id_fkey(
            id,
            name,
            type
          )
        `)
        .eq('user_id', userId)
        .order('added_date', { ascending: false });

      if (botAccountsError) throw botAccountsError;
      if (!botAccountsData || botAccountsData.length === 0) return [];

      // Step 2: Get unique trading account IDs
      const tradingAccountIds = [...new Set(botAccountsData.map(ba => ba.accountid_trading).filter(Boolean))];
      
      if (tradingAccountIds.length === 0) {
        // Return bot accounts without trading account info
        return botAccountsData.map(botAccount => {
          const bot = Array.isArray(botAccount.bots) ? botAccount.bots[0] : botAccount.bots;
          
          return {
            id: botAccount.id,
            botId: botAccount.bot_id,
            tradingAccountId: botAccount.accountid_trading,
            volumeMultiplier: botAccount.volume_multiplier || 1,
            isReversePosition: botAccount.is_reverse_position || (botAccount.bot_id !== botAccount.original_bot_id),
            status: botAccount.status,
            botName: bot?.name,
            botType: bot?.type
          };
        });
      }

      // Step 3: Get trading accounts with API connection and CSP account info
      const { data: tradingAccountsData, error: tradingAccountsError } = await supabase
        .from('trading_accounts')
        .select(`
          accountid_trading,
          balance_account,
          live,
          api_connections!trading_accounts_api_connection_id_fkey(
            id,
            api_name,
            csp_accounts!api_connections_csp_account_id_fkey(
              id,
              broker,
              prop_firm
            )
          )
        `)
        .in('accountid_trading', tradingAccountIds);

      if (tradingAccountsError) throw tradingAccountsError;

      // Step 4: Create lookup map for trading accounts
      const tradingAccountsMap = new Map();
      tradingAccountsData?.forEach(ta => {
        tradingAccountsMap.set(ta.accountid_trading, ta);
      });

      // Step 5: Combine bot accounts with trading account data
      return botAccountsData.map(botAccount => {
        const bot = Array.isArray(botAccount.bots) ? botAccount.bots[0] : botAccount.bots;
        const tradingAccount = tradingAccountsMap.get(botAccount.accountid_trading);
        const apiConnection = tradingAccount && Array.isArray(tradingAccount.api_connections) ? tradingAccount.api_connections[0] : tradingAccount?.api_connections;
        const cspAccount = apiConnection && Array.isArray(apiConnection.csp_accounts) ? apiConnection.csp_accounts[0] : apiConnection?.csp_accounts;
        
        return {
          id: botAccount.id,
          botId: botAccount.bot_id,
          tradingAccountId: botAccount.accountid_trading,
          volumeMultiplier: botAccount.volume_multiplier || 1,
          isReversePosition: botAccount.is_reverse_position || (botAccount.bot_id !== botAccount.original_bot_id),
          status: botAccount.status,
          // Additional info from joins
          botName: bot?.name,
          botType: bot?.type,
          balance: tradingAccount?.balance_account,
          isLive: tradingAccount?.live,
          broker: cspAccount?.broker,
          propFirm: cspAccount?.prop_firm
        };
      });
    } catch (error) {
      console.error('Error fetching user bot accounts:', error);
      return [];
    }
  },

  // AI & Features Tab
  async getUserAIUsage(userId: string): Promise<UserAIUsage> {
    try {
      const [messageUsage, analysisUsage, topicUsage] = await Promise.all([
        // Total messages
        supabase
          .from('user_message_usage')
          .select('message_count')
          .eq('user_id', userId),

        // Analysis sessions
        supabase
          .from('ai_analysis_sessions')
          .select('id, created_at')
          .eq('user_id', userId),

        // Topic-based usage with limits
        supabase
          .from('user_message_usage')
          .select(`
            topic,
            usage_date,
            message_count,
            ai_message_limits(daily_limit, monthly_limit)
          `)
          .eq('user_id', userId)
          .order('usage_date', { ascending: false })
          .limit(30)
      ]);

      const totalMessages = messageUsage.data?.reduce((sum, usage) => sum + usage.message_count, 0) || 0;
      const totalAnalysisSessions = analysisUsage.data?.length || 0;

      // Process topic usage
      const topicMap = new Map();
      topicUsage.data?.forEach(usage => {
        const topic = usage.topic;
        if (!topicMap.has(topic)) {
          topicMap.set(topic, {
            topic,
            messageCount: 0,
            dailyLimit: usage.ai_message_limits?.[0]?.daily_limit || 100,
            monthlyLimit: usage.ai_message_limits?.[0]?.monthly_limit || 1000
          });
        }
        topicMap.get(topic).messageCount += usage.message_count;
      });

      // Get recent usage (last 7 days)
      const sevenDaysAgo = new Date();
      sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);

      const recentUsageMap = new Map();
      for (let i = 0; i < 7; i++) {
        const date = new Date(sevenDaysAgo);
        date.setDate(date.getDate() + i);
        const dateStr = date.toISOString().split('T')[0];
        recentUsageMap.set(dateStr, { date: dateStr, messageCount: 0, analysisCount: 0 });
      }

      topicUsage.data?.forEach(usage => {
        const dateStr = usage.usage_date;
        if (recentUsageMap.has(dateStr)) {
          recentUsageMap.get(dateStr).messageCount += usage.message_count;
        }
      });

      analysisUsage.data?.forEach(session => {
        const dateStr = session.created_at.split('T')[0];
        if (recentUsageMap.has(dateStr)) {
          recentUsageMap.get(dateStr).analysisCount += 1;
        }
      });

      return {
        totalMessages,
        totalAnalysisSessions,
        topicUsage: Array.from(topicMap.values()),
        recentUsage: Array.from(recentUsageMap.values())
      };
    } catch (error) {
      console.error('Error fetching user AI usage:', error);
      return {
        totalMessages: 0,
        totalAnalysisSessions: 0,
        topicUsage: [],
        recentUsage: []
      };
    }
  },

  // Subscription & Billing Tab
  async getUserSubscriptionDetails(userId: string): Promise<UserSubscriptionDetail[]> {
    try {
      const { data, error } = await supabase
        .from('subscriptions')
        .select(`
          id,
          user_id,
          package_id,
          start_date,
          end_date,
          status,
          renewal_type,
          payment_method,
          payment_status,
          packages(name, price_monthly, price_yearly)
        `)
        .eq('user_id', userId)
        .order('created_at', { ascending: false });

      if (error) throw error;

      return data?.map(sub => {
        const packageData = Array.isArray(sub.packages) ? sub.packages[0] : sub.packages;
        return {
          id: sub.id,
          userId: sub.user_id,
          packageId: sub.package_id,
          startDate: sub.start_date,
          endDate: sub.end_date,
          status: sub.status,
          renewalType: sub.renewal_type,
          paymentMethod: sub.payment_method,
          paymentStatus: sub.payment_status,
          packageName: packageData?.name || 'Unknown',
          packagePrice: sub.renewal_type === 'yearly' ? packageData?.price_yearly : packageData?.price_monthly || 0
        };
      }) || [];
    } catch (error) {
      console.error('Error fetching user subscription details:', error);
      return [];
    }
  },

  // Security & Audit Tab
  async getUserLoginHistory(userId: string): Promise<UserLoginHistory[]> {
    try {
      const { data, error } = await supabase
        .from('assistant_sessions')
        .select('id, created_at, updated_at')
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .limit(20);

      if (error) throw error;

      return data?.map(session => ({
        sessionId: session.id,
        createdAt: session.created_at,
        lastActivity: session.updated_at
      })) || [];
    } catch (error) {
      console.error('Error fetching user login history:', error);
      return [];
    }
  },

  // Admin Actions
  async syncUserBalances(userId: string): Promise<boolean> {
    try {
      // Get user's CSP accounts first
      const { data: cspAccounts } = await supabase
        .from('csp_accounts')
        .select('id')
        .eq('user_id', userId);

      if (!cspAccounts?.length) return false;

      // Get API connections for these CSP accounts
      const { data: apiConnections } = await supabase
        .from('api_connections')
        .select('id')
        .in('csp_account_id', cspAccounts.map(acc => acc.id));

      if (!apiConnections?.length) return false;

      // Update balance sync status
      const { error } = await supabase
        .from('api_connections')
        .update({ 
          last_balance_sync: new Date().toISOString(),
          balance_sync_status: 'pending'
        })
        .in('id', apiConnections.map(conn => conn.id));

      return !error;
    } catch (error) {
      console.error('Error syncing user balances:', error);
      return false;
    }
  },

  async refreshUserConnections(userId: string): Promise<boolean> {
    try {
      // Get user's CSP accounts first
      const { data: cspAccounts } = await supabase
        .from('csp_accounts')
        .select('id')
        .eq('user_id', userId);

      if (!cspAccounts?.length) return false;

      // Get API connections for these CSP accounts
      const { data: apiConnections } = await supabase
        .from('api_connections')
        .select('id')
        .in('csp_account_id', cspAccounts.map(acc => acc.id));

      if (!apiConnections?.length) return false;

      // Trigger refresh for these connections
      const { error } = await supabase
        .from('api_connections')
        .update({ 
          last_refresh_attempt: new Date().toISOString(),
          refresh_status: 'pending'
        })
        .in('id', apiConnections.map(conn => conn.id));

      return !error;
    } catch (error) {
      console.error('Error refreshing user connections:', error);
      return false;
    }
  }
};